import React, { useCallback } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { createUseStyles } from 'react-jss';

import { BillingInformation, useUpdateBillingAddressDetails } from '@app/api';
import { ApiValidationFailedException } from 'store/modules/exceptions';
import { Theme } from '@app/theme';

import { SubmitButton, useToast } from '@app/core';
import {
  BillingInformationFormFields,
  billingValidationSchema,
  addressOnlyValidationSchema,
  useBillingInfoInitialValues,
} from './BillingInformationFormFields';
import { useTranslation } from 'react-i18next';

interface Props {
  billingInformation: BillingInformation;
  onSuccess?: (info: BillingInformation) => any;
  submitButton?: React.ReactNode;
  onShouldApplyVatChange?: (val: boolean) => any;
  addressOnly?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 4,
  },
}));

export const BillingInformationForm = ({
  billingInformation,
  onSuccess,
  submitButton,
  onShouldApplyVatChange,
  addressOnly,
}: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { t } = useTranslation();
  const initialValues = useBillingInfoInitialValues(billingInformation);
  const { mutateAsync: updateBillingInformation } =
    useUpdateBillingAddressDetails(billingInformation.id);

  const onSubmit = useCallback(
    async (
      values: Partial<BillingInformation>,
      { setStatus }: FormikHelpers<any>,
    ) => {
      try {
        const res = await updateBillingInformation(values);
        if (onSuccess) {
          onSuccess(res);
        }
        toast.saved();
      } catch (e) {
        toast.exception(e);
        if (e instanceof ApiValidationFailedException) {
          setStatus(e.getFormikErrors());
        }
      }
    },
    [toast, updateBillingInformation, onSuccess],
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={
        addressOnly ? addressOnlyValidationSchema : billingValidationSchema
      }
    >
      <Form>
        <BillingInformationFormFields
          onShouldApplyVatChange={onShouldApplyVatChange}
          initialValues={initialValues}
          addressOnly={addressOnly}
        />

        {submitButton ? (
          submitButton
        ) : (
          <div className={classes.buttonsLine}>
            <SubmitButton text={t('billing.informations.createButton')} />
          </div>
        )}
      </Form>
    </Formik>
  );
};
